import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Truck, Store } from "lucide-react";
import { useApp } from "../context/AppContext";
import { createCheckout } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const COPY = {
  nl: {
    back: "Terug", title: "Afrekenen", contact: "Contactgegevens", shipping: "Verzendadres",
    first: "Voornaam", last: "Achternaam", email: "E-mailadres", phone: "Telefoonnummer",
    company: "Bedrijfsnaam (optioneel)", street: "Straat", house: "Huisnr.", postal: "Postcode",
    city: "Plaats", country: "Land", note: "Opmerking (optioneel)", summary: "Overzicht",
    subtotal: "Subtotaal", shippingLine: "Verzending", free: "Gratis", vat: "incl. btw",
    pay: "Naar betaling", secure: "Beveiligd betalen via Stripe · iDEAL & kaart",
    empty: "Je winkelmand is leeg.", shop: "Verder winkelen",
    method: "Bezorgmethode", deliverShip: "Bezorgen", deliverPickup: "Afhalen in Lelystad",
    shipDesc: "Thuisbezorgd · 1-3 werkdagen", pickupDesc: "Gratis · adres per e-mail",
    pickupBox: "Je ontvangt het afhaaladres in Lelystad per e-mail na je bestelling.",
  },
  en: {
    back: "Back", title: "Checkout", contact: "Contact details", shipping: "Shipping address",
    first: "First name", last: "Last name", email: "Email", phone: "Phone number",
    company: "Company (optional)", street: "Street", house: "No.", postal: "Postal code",
    city: "City", country: "Country", note: "Note (optional)", summary: "Summary",
    subtotal: "Subtotal", shippingLine: "Shipping", free: "Free", vat: "incl. VAT",
    pay: "Continue to payment", secure: "Secure payment via Stripe · iDEAL & card",
    empty: "Your cart is empty.", shop: "Continue shopping",
    method: "Delivery method", deliverShip: "Delivery", deliverPickup: "Pickup in Lelystad",
    shipDesc: "Home delivery · 1-3 business days", pickupDesc: "Free · address by email",
    pickupBox: "You'll receive the pickup address in Lelystad by email after your order.",
  },
};

const COUNTRIES = [
  { c: "NL", nl: "Nederland", en: "Netherlands" },
  { c: "BE", nl: "België", en: "Belgium" },
  { c: "DE", nl: "Duitsland", en: "Germany" },
  { c: "FR", nl: "Frankrijk", en: "France" },
  { c: "LU", nl: "Luxemburg", en: "Luxembourg" },
];

const field = "w-full h-12 px-4 rounded-xl border border-[#E0CDC2] bg-white text-sm focus:outline-none focus:border-[#B4715C]";
const label = "block text-[11px] tracking-label uppercase text-[#8A756C] mb-1.5";

const CheckoutPage = () => {
  const { lang, cart, subtotal } = useApp();
  const c = COPY[lang];
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState("ship"); // "ship" | "pickup"
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "",
    street: "", house_number: "", postal_code: "", city: "", country: "NL", note: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const isPickup = delivery === "pickup";
  const shipping = isPickup ? 0 : (subtotal >= 100 || subtotal === 0 ? 0 : 4.95);
  const total = subtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout(cart, window.location.origin, f, delivery);
      if (checkout_url) window.location.href = checkout_url;
      else throw new Error("no url");
    } catch {
      toast({ title: "LÉLUNE", description: lang === "nl" ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong. Try again." });
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-3xl text-[#3A2C28]">{c.empty}</p>
        <button onClick={() => navigate("/")} className="text-[12px] tracking-label uppercase text-[#B4715C] link-underline">{c.shop}</button>
      </main>
    );
  }

  return (
    <main className="max-w-[1100px] mx-auto px-6 py-12 md:py-16">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[12px] tracking-label uppercase text-[#B4715C] hover:text-[#9E5F4B]">
        <ArrowLeft className="h-4 w-4" /> {c.back}
      </button>
      <h1 className="mt-6 font-serif font-light text-5xl text-[#3A2C28]">{c.title}</h1>

      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
        {/* Form */}
        <div className="space-y-8">
          <div>
            <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-4">{c.contact}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={label}>{c.first}</label><input required value={f.first_name} onChange={set("first_name")} className={field} /></div>
              <div><label className={label}>{c.last}</label><input required value={f.last_name} onChange={set("last_name")} className={field} /></div>
              <div><label className={label}>{c.email}</label><input required type="email" value={f.email} onChange={set("email")} className={field} /></div>
              <div><label className={label}>{c.phone}</label><input required value={f.phone} onChange={set("phone")} className={field} /></div>
              <div className="sm:col-span-2"><label className={label}>{c.company}</label><input value={f.company} onChange={set("company")} className={field} /></div>
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-4">{c.method}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button type="button" onClick={() => setDelivery("ship")} data-testid="delivery-ship-option"
                className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-colors ${!isPickup ? "border-[#B4715C] bg-[#FBF3EE]" : "border-[#E0CDC2] bg-white hover:border-[#D3B9AC]"}`}>
                <Truck className={`h-5 w-5 mt-0.5 ${!isPickup ? "text-[#B4715C]" : "text-[#A38F86]"}`} />
                <span>
                  <span className="block text-sm text-[#3A2C28]">{c.deliverShip}</span>
                  <span className="block text-[12px] text-[#8A756C]">{c.shipDesc}</span>
                </span>
              </button>
              <button type="button" onClick={() => setDelivery("pickup")} data-testid="delivery-pickup-option"
                className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-colors ${isPickup ? "border-[#B4715C] bg-[#FBF3EE]" : "border-[#E0CDC2] bg-white hover:border-[#D3B9AC]"}`}>
                <Store className={`h-5 w-5 mt-0.5 ${isPickup ? "text-[#B4715C]" : "text-[#A38F86]"}`} />
                <span>
                  <span className="block text-sm text-[#3A2C28]">{c.deliverPickup}</span>
                  <span className="block text-[12px] text-[#8A756C]">{c.pickupDesc}</span>
                </span>
              </button>
            </div>
          </div>

          {!isPickup ? (
            <div>
              <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-4">{c.shipping}</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2"><label className={label}>{c.street}</label><input required value={f.street} onChange={set("street")} className={field} /></div>
                <div><label className={label}>{c.house}</label><input required value={f.house_number} onChange={set("house_number")} className={field} /></div>
                <div><label className={label}>{c.postal}</label><input required value={f.postal_code} onChange={set("postal_code")} className={field} /></div>
                <div><label className={label}>{c.city}</label><input required value={f.city} onChange={set("city")} className={field} /></div>
                <div>
                  <label className={label}>{c.country}</label>
                  <select value={f.country} onChange={set("country")} className={field}>
                    {COUNTRIES.map((co) => <option key={co.c} value={co.c}>{co[lang]}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 rounded-xl border border-[#E7D8CD] bg-[#FBF3EE]" data-testid="pickup-info-box">
              <Store className="h-5 w-5 mt-0.5 text-[#B4715C] shrink-0" />
              <p className="text-sm text-[#6E5A52]">{c.pickupBox}</p>
            </div>
          )}

          <div>
            <label className={label}>{c.note}</label>
            <textarea rows={2} value={f.note} onChange={set("note")} className="w-full px-4 py-3 rounded-xl border border-[#E0CDC2] bg-white text-sm focus:outline-none focus:border-[#B4715C]" />
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-[#EEE0D7] p-6 lg:sticky lg:top-24">
          <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-4">{c.summary}</p>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {cart.map((l) => (
              <div key={l.key} className="flex gap-3">
                <div className="h-14 w-12 rounded-lg overflow-hidden bg-[#F3E7DF] shrink-0"><img src={l.image} alt={l.name[lang]} className="h-full w-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[15px] text-[#3A2C28] leading-tight">{l.name[lang]}</p>
                  {l.shade && <p className="text-[11px] tracking-label uppercase text-[#8A756C]">tint {l.shade}</p>}
                  <p className="text-[12px] text-[#8A756C]">{l.qty}× €{l.price.toFixed(2)}</p>
                </div>
                <span className="text-sm text-[#3A2C28]">€{(l.price * l.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-[#EEE0D7] space-y-2 text-sm">
            <div className="flex justify-between text-[#6E5A52]"><span>{c.subtotal}</span><span>€{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-[#6E5A52]"><span>{isPickup ? c.deliverPickup : c.shippingLine}</span><span>{shipping === 0 ? c.free : `€${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between items-baseline pt-2 border-t border-[#EEE0D7]">
              <span className="text-[11px] tracking-label uppercase text-[#8A756C]">Totaal <span className="lowercase">({c.vat})</span></span>
              <span className="font-serif text-2xl text-[#3A2C28]">€{total.toFixed(2)}</span>
            </div>
          </div>
          <button type="submit" disabled={loading} className="mt-6 w-full h-12 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C] disabled:opacity-70 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {c.pay}
          </button>
          <p className="mt-3 text-[11px] text-center text-[#A38F86]">{c.secure}</p>
        </div>
      </form>
    </main>
  );
};

export default CheckoutPage;
